<template>
  <div class="scan-action-cell">
    <custom-tooltip :content="tooltip">
      <span
        slot="buttonContent"
        class="scan-action-cell__action"
        :class="[`scan-action-cell__action--${actionType}`, { 'scan-action-cell__action--loading': inProgress }]"
        @click.stop="runAction"
      >
        <v-progress-circular v-if="inProgress" indeterminate :size="16" :width="2" />
        <v-icon v-else class="scan-action-cell__icon">{{ icon }}</v-icon>
      </span>
    </custom-tooltip>
  </div>
</template>

<script>
import { projectStatuses } from '@/constants/projects';
import { RETRY_SCAN, STOP_SCAN } from '@/store/project/actionTypes';
import { SET_PROJECT_STATUS } from '@/store/project/mutationTypes';
import CustomTooltip from '@/components/common/CustomTooltip.vue';

const ACTION = {
  RETRY: 'retry',
  STOP: 'stop',
};

export default {
  name: 'ProjectScanActionTableCell',
  components: { CustomTooltip },
  props: {
    item: {
      type: Object,
      required: true,
    },
    column: {
      type: Object,
      required: false,
    },
  },
  data() {
    return {
      inProgress: false,
    };
  },
  computed: {
    actionType() {
      if ([projectStatuses.CANCELED, projectStatuses.FAILED].includes(this.item.status)) {
        return ACTION.RETRY;
      }
      if ([projectStatuses.IN_PROGRESS, projectStatuses.REPEAT_IN_PROGRESS].includes(this.item.status)) {
        return ACTION.STOP;
      }
      return null;
    },
    icon() {
      return this.actionType === ACTION.STOP ? 'stop' : 'refresh';
    },
    tooltip() {
      return this.actionType === ACTION.STOP
        ? this.$t('activity.log.action.stop')
        : this.$t('configuration.retry.scan.tooltip');
    },
  },
  methods: {
    async runAction() {
      if (this.inProgress || !this.actionType) {
        return;
      }
      const previousStatus = this.item.status;
      const isStop = this.actionType === ACTION.STOP;
      const action = isStop ? STOP_SCAN : RETRY_SCAN;
      // Optimistically reflect the new status so the row updates instantly,
      // without re-fetching the whole list. A retry goes back through the full
      // scan cycle, so it starts in PENDING (not in-progress) — matching the
      // backend, which queues it for the scheduler before it actually runs.
      const optimisticStatus = isStop ? projectStatuses.CANCELED : projectStatuses.PENDING;

      this.inProgress = true;
      this.$store.commit(SET_PROJECT_STATUS, { projectId: this.item.id, status: optimisticStatus });
      try {
        await this.$store.dispatch(action, { projectId: this.item.id });
      } catch (e) {
        // Revert on failure.
        this.$store.commit(SET_PROJECT_STATUS, { projectId: this.item.id, status: previousStatus });
        this.$emit('displayError', e.message);
      } finally {
        this.inProgress = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@import '~@/assets/styles/design-system';

// Revealed on row hover (same trigger the severity stats use), shown in the
// status column in place of the status badge.
.scan-action-cell {
  display: var(--displayissuecountcell, none);

  &__action {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    height: rem(28);
    width: rem(28);
    border-radius: $radius-md;
    background-color: var(--brand-primary-bg);
    cursor: pointer;

    &--stop {
      background-color: var(--severity-critical-bg);

      .scan-action-cell__icon {
        color: var(--severity-critical) !important;
      }

      .v-progress-circular {
        color: var(--severity-critical);
      }
    }

    &--loading {
      cursor: default;
    }

    .v-progress-circular {
      color: var(--brand-primary);
    }
  }

  &__icon {
    font-size: $font-size-md;
    color: var(--brand-primary) !important;
  }
}
</style>
